
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/layout/Header";
import { useState, useEffect } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, ChevronDown, History, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { DailyRecords } from "@/components/nurse/DailyRecords";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export default function HistoryPage() {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [date, setDate] = useState<Date>(new Date());
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { toast } = useToast();

  // This ensures the date is properly initialized to today when the component mounts
  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    setDate(today);
  }, []);

  // Query to get the current active term
  const { data: activeTerm } = useQuery({
    queryKey: ['active-term', user?.schoolId],
    queryFn: async () => {
      if (!user?.schoolId) return null;
      const { data, error } = await supabase
        .from('terms')
        .select('*')
        .eq('school_id', user.schoolId)
        .eq('is_active', true)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching active term:', error);
        return null;
      }
      return data;
    },
    enabled: !!user?.schoolId
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeTerm) {
      toast({
        title: "No active term",
        description: "There is no active term selected. Please set an active term first.",
        variant: "destructive"
      });
      return;
    }
    if (searchTerm.trim()) {
      toast({
        title: "Searching Visits",
        description: `Searching for "${searchTerm}" in visits for ${format(date, "dd MMM yyyy")}`
      });
    }
  };

  // Handle keydown event for the search input to trigger search on Enter key
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch(e as unknown as React.FormEvent);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container max-w-7xl px-4 pt-28 pb-16">
        <div className="space-y-6 my-[68px]">
          <h1 className="text-3xl font-bold mb-6 flex items-center gap-2">
            <History className="h-8 w-8 text-primary" />
            Visit History
          </h1>

          <Card>
            <Collapsible open={isSearchOpen} onOpenChange={setIsSearchOpen}>
              <CollapsibleTrigger className="w-full">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Search className="h-5 w-5 text-primary" />
                      <h2 className="text-xl font-semibold">Search All Term Visits</h2>
                    </div>
                    <ChevronDown className={cn("h-5 w-5 text-muted-foreground transition-transform", {
                      "transform rotate-180": isSearchOpen
                    })} />
                  </div>
                </CardContent>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0 px-6 pb-6">
                  <form onSubmit={handleSearch}>
                    <div className="flex items-center border rounded-md overflow-hidden">
                      <div className="pl-3">
                        <Search className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <Input
                        type="text"
                        className="border-0 flex-1 focus-visible:ring-0 focus-visible:ring-offset-0"
                        placeholder="Search all visits by name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={handleKeyDown}
                      />
                    </div>
                  </form>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          <div className="grid grid-cols-1 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2 mb-6">
                  <CalendarIcon className="h-5 w-5 text-primary" />
                  <h2 className="text-xl font-semibold">Filter By Date</h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {date ? format(date, "dd MMM yyyy") : "Select date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={date}
                          onDateSelect={(selectedDate) => {
                            setDate(selectedDate);
                            setIsCalendarOpen(false);
                          }}
                          initialFocus
                          className="pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  <div className="flex items-center border rounded-md overflow-hidden">
                    <div className="pl-3">
                      <Search className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <Input
                      type="text"
                      className="border-0 flex-1 focus-visible:ring-0 focus-visible:ring-offset-0"
                      placeholder={`Search ${format(date, "dd MMM yyyy")} visits by name...`}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Daily Records component with enhanced searchTerm support */}
            <DailyRecords 
              schoolId={user?.schoolId} 
              searchTerm={searchTerm} 
              startDate={date} 
              termId={activeTerm?.id} 
            />
          </div>
        </div>
      </main>
    </div>
  );
}
